import axios from 'axios';
import { v4 as uuid } from 'uuid';
import React, { useCallback, useState } from 'react';
import { makeUseAxios, UseAxios } from 'axios-hooks';
import { useActionItem, useRelated } from '@workboard/action-item';

import { useCsrfToken } from '../utils/useCsrfToken';
import { AILink } from '../ai/link';
import { useNotification } from '../notifications';
import { FormData } from './useCopyAIForm';
import { useTracking } from '../utils/useTracking';
import * as events from './tracking';
import { getGlobalConfig } from '../global/ConfigProvider';

export function useCopyAI(id: string, close: () => void) {
  const { success: notifySuccess, error: notifyError } = useNotification();
  const { baseApiUrl } = getGlobalConfig();
  const rawBaseUrl = baseApiUrl ?? (window.wbConfig || {}).baseUrl;
  const baseURL = typeof rawBaseUrl === 'string' ? rawBaseUrl : '';
  const [isProcessing, setProcessing] = useState(false);
  const csrfToken: string = useCsrfToken();
  const useAxiosInstance: UseAxios = makeUseAxios({
    axios: axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
        'YII-CSRF-TOKEN': csrfToken,
      },
    }),
  });
  const [, request] = useAxiosInstance(
    {
      url: 'pmWorkstream/CopyAndLinkActionItem',
      method: 'POST',
    },
    { manual: true }
  );
  const { types } = useRelated(id);
  const { actionItem } = useActionItem(id);
  const activityTypeName = actionItem?.typeAttributes?.label ?? 'AI';
  const trackEvent = useTracking();
  const submit = useCallback(
    async (data: FormData) => {
      const {
        title,
        effort,
        priority,
        createNew,
        confidence,
        assigneeId,
        workstreamId,
        stateId: state,
        relationshipType,
        dueDateTimestamp,
        fromDateTimestamp,
      } = data;
      try {
        const copiedToSourceActionItemRelationshipId = relationshipType
          ? uuid()
          : null;
        const copiedToSourceActionItemRelationshipTypeId =
          relationshipType?.split(':')?.[0] ?? null;
        setProcessing(true);
        const {
          data: { createdActionItemId: aiId },
        } = await request({
          data: {
            sourceActionItemId: Number(id),
            actionItemModifications: {
              title,
              state,
              effort,
              priority,
              createNew,
              confidence,
              assigneeId: Number(assigneeId),
              dueDate: dueDateTimestamp ?? null,
              startDate: fromDateTimestamp ?? null,
            },
            copiedToSourceActionItemRelationshipId,
            targetWorkstreamId: Number(workstreamId),
            copiedToSourceActionItemRelationshipTypeId,
          },
        });
        trackEvent(events.dialogSubmitted, { parentId: id, ...data, id: aiId });
        notifySuccess(
          <>
            New {activityTypeName} <AILink id={aiId} hellip={false} /> created
          </>
        );
        if (!data.createNew) {
          close();
        }
      } catch (e) {
        notifyError(
          `There was an error while copying ${activityTypeName}. Please try again later`
        );
      } finally {
        setProcessing(false);
      }
    },
    [id, close, notifySuccess, notifyError, trackEvent, request, activityTypeName]
  );
  return { isProcessing, types, submit };
}
